import { useState, useEffect } from "react";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import CrossLarge from "../design-system/icons/CrossLarge.jsx";
import useAuthedApi from "../hooks/useAuthedApi";

const RISK_ACK_KEY = "xenorize_leverage_risk_ack";
const SPOT_URL = process.env.REACT_APP_SPOT_URL || "https://stg2.xenorize.com/dashboard";

const RISK_BULLETS = [
  "Leverage trading involves higher risk than Spot trading. Please read the following before continuing.",
  "Leverage amplifies both gains and losses.",
  "You can lose more than your initial allocation.",
  "Xenorize does not manage or protect your positions.",
  "You are responsible for your leverage settings and risk.",
];

export default function RiskAcknowledgementModal() {
  const { authedGet, authedPost } = useAuthedApi();
  const [show, setShow] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Fast path: localStorage cache means this user already acknowledged on
    // this device — skip the API call entirely.
    if (localStorage.getItem(RISK_ACK_KEY) === "1") return;

    // Slow path: check backend so acknowledgement persists across devices.
    authedGet("/api/borrow/risk-ack/")
      .then((res) => {
        if (res.data.acknowledged) {
          localStorage.setItem(RISK_ACK_KEY, "1");
        } else {
          setShow(true);
        }
      })
      .catch(() => {
        // If the API call fails, show the modal so the user isn't locked out.
        setShow(true);
      });
  }, []);

  if (!show) return null;

  const handleAccept = async () => {
    try {
      await authedPost("/api/borrow/risk-ack/", {});
    } catch (_) {}
    localStorage.setItem(RISK_ACK_KEY, "1");
    setShow(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <a
          href={SPOT_URL}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          title="Go back to Spot"
        >
          <CrossLarge width={20} height={20} />
        </a>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <HiOutlineExclamationTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#21272A]">
              Before you enter Leverage Market
            </h3>
            <p className="text-sm text-[#697077]">Read carefully on below</p>
          </div>
        </div>

        <ul className="space-y-2.5 mb-6">
          {RISK_BULLETS.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#21272A]">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#21272A] shrink-0" />
              {b}
            </li>
          ))}
        </ul>

        <hr className="border-gray-100 mb-4" />

        <label className="flex items-start gap-3 mb-6 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-[#302571]"
          />
          <span className="text-sm font-semibold text-[#21272A]">
            I understand the risks of Leverage Market trading.
          </span>
        </label>

        <div className="flex items-center gap-3">
          <a
            href={SPOT_URL}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-[#21272A] hover:bg-gray-50 transition text-center"
          >
            Stay in Spot Market
          </a>
          <button
            onClick={handleAccept}
            disabled={!checked}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition ${
              checked
                ? "bg-[#403297] hover:bg-[#302571]"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            Continue to Leverage
          </button>
        </div>
      </div>
    </div>
  );
}
